import React, { useState } from "react";
import { Box, TextField, MenuItem, Button, Grid, CircularProgress, Typography, IconButton, Divider, Stack } from "@mui/material";
import DeleteIcon from "@mui/icons-material/Delete";
import AddIcon from "@mui/icons-material/Add";
import { useAddClinicalRecord } from "store/hooks/react-query/query/useClinicalRecords";
import { toast } from "react-toastify";

const RECORD_TYPES = [
  { value: "VITAL_SIGNS", label: "Vital Signs Chart" },
  { value: "FLUID_BALANCE", label: "Fluid Balance Chart" },
  { value: "CLINICAL_NOTE", label: "Clinical Note (Dr/Nurse)" },
  { value: "MEDICATION", label: "Medication Administration" },
];

export default function ClinicalRecordForm({ patientId, onSuccess }) {
  const [recordType, setRecordType] = useState("VITAL_SIGNS");
  const { mutate, isLoading } = useAddClinicalRecord();

  // Dynamic state for medications
  const [medications, setMedications] = useState([{ medicationName: "", dose: "", frequency: "" }]);

  const handleMedChange = (index, field, value) => {
    const updated = [...medications];
    updated[index][field] = value;
    setMedications(updated);
  };

  const addMedRow = () => setMedications([...medications, { medicationName: "", dose: "", frequency: "" }]);
  const removeMedRow = (index) => setMedications(medications.filter((_, i) => i !== index));

  const handleSubmit = (e) => {
    e.preventDefault();
    const formData = new FormData(e.target);
    const data = Object.fromEntries(formData.entries());

    let payload = { patientId, recordType, ...data };

    // Format specific number fields
    if (recordType === 'FLUID_BALANCE') {
      payload.inputVolume = Number(data.inputVolume);
      payload.outputVolume = Number(data.outputVolume);
    }
    if (recordType === 'VITAL_SIGNS') {
      payload.temp = Number(data.temp);
      payload.pulse = Number(data.pulse);
      payload.resp = Number(data.resp);
      payload.spo2 = Number(data.spo2);
      payload.rbs = Number(data.rbs);
    }
    if (recordType === 'MEDICATION') {
      // For medication, we ignore the form data and use our dynamic state
      // Filter out empty medication names
      payload = { 
        patientId, 
        recordType, 
        medications: medications.filter(m => m.medicationName.trim() !== "") 
      };
      if (payload.medications.length === 0) return toast.error("Please enter at least one medication");
    }

    mutate(payload, { 
      onSuccess: () => {
        toast.success("Record added successfully");
        if (onSuccess) onSuccess(); 
      },
      onError: () => toast.error("Failed to add record")
    });
  };

  return (
    <Box component="form" onSubmit={handleSubmit} sx={{ p: 2 }}>
      <TextField
        select fullWidth label="Select Log Type"
        value={recordType}
        onChange={(e) => setRecordType(e.target.value)}
        sx={{ mb: 3 }}
      >
        {RECORD_TYPES.map((t) => <MenuItem key={t.value} value={t.value}>{t.label}</MenuItem>)}
      </TextField>

      <Grid container spacing={2}>
        {/* --- VITAL SIGNS INPUTS --- */}
        {recordType === "VITAL_SIGNS" && (
          <>
            <Grid item xs={6}><TextField name="bp" label="BP (mmHg)" placeholder="120/80" fullWidth required /></Grid>
            <Grid item xs={6}><TextField name="temp" label="Temp (°C)" type="number" inputProps={{ step: "0.1" }} fullWidth required /></Grid>
            <Grid item xs={6}><TextField name="pulse" label="Pulse (bpm)" type="number" fullWidth required /></Grid>
            <Grid item xs={6}><TextField name="resp" label="Resp (cpm)" type="number" fullWidth required /></Grid>
            <Grid item xs={6}><TextField name="spo2" label="SPO2 (%)" type="number" fullWidth required /></Grid>
            <Grid item xs={6}><TextField name="rbs" label="RBS (mg/dL)" type="number" fullWidth required /></Grid>
          </>
        )}

        {/* --- MEDICATION INPUTS (DYNAMIC) --- */}
        {recordType === "MEDICATION" && (
          <Grid item xs={12}>
            {medications.map((med, index) => (
              <Box key={index} sx={{ mb: 2, p: 2, border: "1px dashed #ccc", borderRadius: 2 }}>
                <Stack direction="row" justifyContent="space-between" alignItems="center" mb={1}>
                  <Typography variant="subtitle2">Medication {index + 1}</Typography>
                  {medications.length > 1 && (
                    <IconButton size="small" color="error" onClick={() => removeMedRow(index)}>
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  )}
                </Stack>
                <Grid container spacing={1}>
                  <Grid item xs={12}><TextField label="Medication Name" required fullWidth size="small" value={med.medicationName} onChange={(e) => handleMedChange(index, "medicationName", e.target.value)} /></Grid>
                  <Grid item xs={6}><TextField label="Dosage (Optional)" fullWidth size="small" value={med.dose} onChange={(e) => handleMedChange(index, "dose", e.target.value)} /></Grid>
                  <Grid item xs={6}><TextField label="Freq (Optional)" fullWidth size="small" value={med.frequency} onChange={(e) => handleMedChange(index, "frequency", e.target.value)} /></Grid>
                </Grid>
              </Box>
            ))}
            <Button startIcon={<AddIcon />} onClick={addMedRow} variant="outlined" fullWidth sx={{ mt: 1 }}>
              Add Another Medication
            </Button>
          </Grid>
        )}

        {/* FLUID & CLINICAL NOTES RE-USED EXACTLY FROM PREVIOUS HERE... */}
        {recordType === "FLUID_BALANCE" && (
           <>
             <Grid item xs={6}><TextField name="inputType" label="Input (e.g. Saline)" fullWidth /></Grid>
             <Grid item xs={6}><TextField name="inputVolume" label="Vol (ml)" type="number" fullWidth /></Grid>
             <Grid item xs={6}><TextField name="outputType" label="Output (e.g. Urine)" fullWidth /></Grid>
             <Grid item xs={6}><TextField name="outputVolume" label="Vol (ml)" type="number" fullWidth /></Grid>
           </>
        )}
        {recordType === "CLINICAL_NOTE" && (
           <>
              <Grid item xs={12}><TextField name="authorName" label="Staff Name" required fullWidth /></Grid>
              <Grid item xs={12}><TextField select name="role" label="Role" defaultValue="NURSE" fullWidth>
                 <MenuItem value="DOCTOR">Doctor</MenuItem>
                 <MenuItem value="NURSE">Nurse</MenuItem>
              </TextField></Grid>
              <Grid item xs={12}><TextField name="note" label="Observation/Report" multiline rows={4} required fullWidth /></Grid>
           </>
        )}
      </Grid>

      <Button type="submit" variant="contained" fullWidth disabled={isLoading} sx={{ mt: 4 }}>
        {isLoading ? <CircularProgress size={24} color="inherit" /> : "Save Record"}
      </Button>
    </Box>
  );
}